import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import {
  DropZoneErrorText,
  DropZoneErrorTypeEnum,
  DROPZONE_TEXT,
  UploadCsvStatusEnum,
  WIDTH_MODAL_COMPONENTS,
} from '../../constants/addressBook';
import { fileRejectionErrorCode } from '../../constants/upload';

interface IProps {
  status: UploadCsvStatusEnum;
  onFileAccepted: (file: File) => void;
  dataAction?: string;
  handleFileSizeError: () => void;
  maxSize: number;
}

interface IErrorText {
  title: string;
  subtitle?: string;
  requiredFieldsList?: string;
}

const Dropzone: React.FC<IProps> = ({ onFileAccepted, status, dataAction, handleFileSizeError, maxSize }) => {
  const { sass, palette } = useTheme();
  const [isRejected, setIsRejected] = useState(false);
  const [errorText, setErrorText] = React.useState<IErrorText | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        setErrorText(DropZoneErrorText[DropZoneErrorTypeEnum.UnsupportedFile]);
        return;
      }

      const validFile = acceptedFiles[0];
      setErrorText(null);
      setIsRejected(false);
      onFileAccepted?.(validFile);
    },
    [onFileAccepted],
  );

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      const errorCode = fileRejections[0].errors[0].code;

      switch (true) {
        case errorCode === fileRejectionErrorCode.fileTooLarge:
          handleFileSizeError();
          setIsRejected(false);
          setErrorText(null);
          break;
        default:
          setIsRejected(true);
          break;
      }
    },
    [handleFileSizeError],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: '.csv, text/csv',
    multiple: false,
    minSize: 1,
    maxSize,
  });

  const hasInvalidHeaders = useMemo(() => {
    return status === UploadCsvStatusEnum.ErrorInvalidHeaders;
  }, [status]);

  const hasNoRowsWithData = useMemo(() => {
    return status === UploadCsvStatusEnum.ErrorNoRowsWithData;
  }, [status]);

  useEffect(() => {
    if (hasInvalidHeaders) {
      setIsRejected(true);
      setErrorText(DropZoneErrorText[DropZoneErrorTypeEnum.InvalidHeaders]);
    }
    if (hasNoRowsWithData) {
      setIsRejected(true);
      setErrorText(DropZoneErrorText[DropZoneErrorTypeEnum.NoRowsWithData]);
    }
  }, [hasInvalidHeaders, hasNoRowsWithData]);

  const getBorderStyle = useMemo(() => {
    if (isRejected) {
      return {
        border: '1px solid',
        borderColor: 'error.main',
        bgcolor: alpha(palette.error.main, 0.04),
      };
    }
    if (isDragActive) {
      return {
        border: '1px dashed',
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
      };
    }
    return {
      border: '1px dashed',
      borderColor: 'grey.200',
      bgcolor: 'background.paper',
    };
  }, [isRejected, isDragActive, palette.error.main]);

  return (
    <Paper
      {...getRootProps()}
      elevation={0}
      sx={{
        maxWidth: WIDTH_MODAL_COMPONENTS,
        mx: 'auto',
        p: 4,
        borderWidth: 2,
        borderRadius: 0.5,
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: isRejected ? undefined : `rgba(${sass.globalAccentColor}, 0.1)`,
          borderColor: isRejected ? undefined : 'primary.main',
        },
        ...getBorderStyle,
      }}
      data-action={dataAction}
    >
      <input {...getInputProps()} />
      <Stack spacing={1} alignItems="center">
        <UploadFileIcon sx={{ width: 24, height: 24, color: isRejected ? 'error.main' : 'primary.main' }} />
        <Typography sx={{ fontSize: 16 }}>
          <Box
            component="span"
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              textUnderlineOffset: 4,
              textDecorationColor: `${palette.primary.main}80`,
            }}
          >
            Browse
          </Box>
          <Box component="span"> or drag and drop</Box>
        </Typography>

        <Stack alignItems="flex-start">
          <Typography variant="body2" sx={{ color: isRejected ? 'error.main' : 'text.secondary' }}>
            {isRejected ? errorText?.title : DROPZONE_TEXT}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default Dropzone;
